import html2pdf from "html2pdf.js";

/**
 * PDF helpers for the invoice view.
 *
 * These were ~120 duplicated lines inside handleSendInvoice and
 * handleSendAllInvoices. Keeping them here means a change to the page
 * size or margins applies to both paths, and neither one can drift.
 */

const PDF_OPTIONS = {
  margin: [0.35, 0.35, 0.45, 0.35],
  image: { type: "jpeg", quality: 0.98 },
  html2canvas: {
    scale: 2,
    useCORS: true,
    allowTaint: false,
    logging: false,
  },
  jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
  pagebreak: { mode: ["css", "legacy"] },
};

/**
 * Waits for ReviewInvoice to render into #pdf-render-area, then returns
 * the PDF as base64.
 */
export const buildInvoicePdf = async (invoiceNumber) => {
  await new Promise((resolve) => {
    setTimeout(resolve, 1200);
  });

  const element = document.getElementById("pdf-render-area");

  if (!element) {
    throw new Error("Invoice PDF area could not be rendered.");
  }

  const pdfData = await html2pdf()
    .set({ ...PDF_OPTIONS, filename: `Invoice-${invoiceNumber}.pdf` })
    .from(element)
    .output("datauristring");

  if (!pdfData) {
    throw new Error("Unable to generate PDF.");
  }

  const base64Pdf = pdfData.split(",")[1];

  if (!base64Pdf) {
    throw new Error("Unable to read generated PDF.");
  }

  return base64Pdf;
};

/**
 * Fills in img_url from whichever key the API happened to return.
 */
export const normalizeInvoiceItems = (items = []) =>
  (Array.isArray(items) ? items : []).map((item) => ({
    ...item,
    img_url:
      item?.img_url ||
      item?.image_url ||
      item?.imageUrl ||
      item?.image ||
      item?.photo_url ||
      item?.photo ||
      item?.img ||
      null,
  }));

/**
 * Works out the customer, lots, image option and invoice number for one
 * row, from the full invoice response. Pure -- it returns values instead
 * of calling setState, so the caller decides what to do with them.
 */
export const buildRenderPayload = (row, details, imageOptions, fallbackEventName) => {
  const invoiceId = row.id;

  const invoiceData = details.data?.invoice || {};
  const invoiceItems = normalizeInvoiceItems(details.data?.items || []);
  const invoiceAddons = details.data?.addons || [];

  const customerName =
    invoiceData.name || row.name || row.customer_name || row.client_name || "";

  const email = invoiceData.email || row.email || row.customer_email || "";

  if (!email || !String(email).trim()) {
    throw new Error("This invoice has no customer email.");
  }

  const lotNumbers = invoiceItems
    .map((item) => item.lot_number)
    .filter(
      (lot) => lot !== undefined && lot !== null && String(lot).trim() !== ""
    )
    .map((lot) => String(lot).trim());

  if (lotNumbers.length === 0) {
    throw new Error("No lot items found for this invoice.");
  }

  const includeImage =
    imageOptions[invoiceId] !== undefined
      ? imageOptions[invoiceId]
      : Boolean(
          invoiceData.with_image !== undefined
            ? invoiceData.with_image
            : row.with_image !== undefined
            ? row.with_image
            : true
        );

  const invoiceNumber =
    invoiceData.invoice_no ||
    row.invoice_no ||
    row.invoice_number ||
    row.receipt_number ||
    `invoice-${invoiceId}`;

  return {
    invoiceId,
    invoiceNumber,
    customerName,
    email,
    includeImage,
    lotNumbers: lotNumbers.join(","),
    eventName: row.event_name || row.auction_name || fallbackEventName || "",
    invoice: {
      ...row,
      ...invoiceData,
      items: invoiceItems,
      addons: invoiceAddons,
    },
  };
};
